import { faker } from '@faker-js/faker';
import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

import { Schedule } from '../types';

export const getSchedule = (): Promise<Schedule.Response> => {
  return axios.get('/user/v2/sessions');
};

const generateReadableDate = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const generateTime = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

const generateDay = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
  }).format(date);
};

const generateNames = (length: number): string => {
  return faker.lorem.words(length);
};

// Constants for schedule generation
const SUBJECTS: ('math' | 'english' | 'hobby_class' | 'master_class')[] = [
  'math',
  'english',
  'hobby_class',
  'master_class',
];

const SUBJECT_TITLES = ['Math', 'English', 'Hobby', 'Master'];

const HOBBY_CLASS_NAMES = [
  'Rubik Cube',
  'Chess Fundamentals',
  'Puzzle Solving',
  'Coffee Painting',
  'Abstract Painting',
  'Calligraphy',
  'Mandala',
  'Hand Craft',
];

// Helper to get lesson name based on variant
const getLessonName = (
  variant: (typeof SUBJECTS)[number],
  subjectIndex: number,
  isUpcoming: boolean,
  lessonNumber: number
): string => {
  if (variant === 'hobby_class') {
    return faker.helpers.arrayElement(HOBBY_CLASS_NAMES);
  }

  if (isUpcoming) {
    return `${SUBJECT_TITLES[subjectIndex]} Lesson ${lessonNumber}`;
  }

  return generateNames(10);
};

// Helper to determine lesson status
const getLessonStatus = (
  isPast: boolean,
  isHoliday: boolean
): Schedule.Lesson['status'] => {
  if (isPast) {
    return faker.helpers.weightedArrayElement([
      { weight: 7, value: 'attended' as const },
      { weight: 2, value: 'missed' as const },
      { weight: 1, value: 'cancelled' as const },
    ]);
  }

  if (isHoliday) {
    return 'holiday';
  }

  return 'upcoming';
};

// Helper to generate a single lesson
const generateLesson = (
  date: Date,
  variant: (typeof SUBJECTS)[number],
  subjectIndex: number,
  isPast: boolean,
  lessonNumber: number
): Schedule.Lesson => {
  const isHoliday =
    !isPast && faker.helpers.maybe(() => true, { probability: 0.15 });
  const lessonName = isHoliday
    ? 'Holiday Break'
    : getLessonName(variant, subjectIndex, !isPast, lessonNumber);

  const status = getLessonStatus(isPast, isHoliday ?? false);

  return {
    name: lessonName,
    date: generateReadableDate(date),
    day: generateDay(date),
    ...(isHoliday ? {} : { time: generateTime(date) }),
    start_time: Math.floor(date.getTime() / 1000),
    status,
    variant: isHoliday ? undefined : variant,
  };
};

// Helper to check if lesson should be included
const shouldIncludeLesson = (
  variant: (typeof SUBJECTS)[number],
  includeHobbyClass: boolean
): boolean => {
  return variant !== 'hobby_class' || includeHobbyClass;
};

// Helper to generate lessons for a time period
const generateLessons = (
  count: number,
  isPast: boolean,
  includeHobbyClass: boolean,
  now: Date
): Schedule.Lesson[] => {
  const lessons: Schedule.Lesson[] = [];
  const startIndex = isPast ? count : 1;
  const endIndex = isPast ? 1 : count;
  const direction = isPast ? -1 : 1;

  for (
    let i = startIndex;
    isPast ? i >= endIndex : i <= endIndex;
    i += direction
  ) {
    const dayOffset = isPast ? -i : i;
    const date = new Date(now.getTime() + dayOffset * 24 * 60 * 60 * 1000);
    const subjectIndex = i % 4;
    const currentSubject = SUBJECTS[subjectIndex];

    if (!shouldIncludeLesson(currentSubject, includeHobbyClass)) {
      continue;
    }

    const lesson = generateLesson(
      date,
      currentSubject,
      subjectIndex,
      isPast,
      isPast ? i : i + 15
    );
    lessons.push(lesson);
  }

  return lessons;
};

const generateRandomScheduleResponse = (): Schedule.Response => {
  const now = new Date();
  const includeHobbyClass =
    faker.helpers.maybe(() => true, { probability: 1 }) ?? true;

  const pastLessons = generateLessons(15, true, includeHobbyClass, now);
  const upcomingLessons = generateLessons(15, false, includeHobbyClass, now);

  return {
    status: 'success',
    message: 'Schedule fetched successfully',
    lessons: [...pastLessons, ...upcomingLessons],
    timezone: 'Asia/Kolkata',
  };
};

export const scheduleMockHandler = http.get(
  `${env.API_URL}/user/v2/sessions`,
  async (): Promise<HttpResponse<Schedule.Response>> => {
    await networkDelay();
    const response = generateRandomScheduleResponse();
    return HttpResponse.json(response);
  }
);
